/**
 * The payment step.
 *
 * Reached from Checkout once the order exists (state `pending_payment`). The
 * order is the source of the amount; this page only collects the card. The
 * order moves to `paid` from the Stripe webhook on the server, never from
 * anything that happens here — so after the card is authorised this page polls
 * its own order until the webhook has landed, then hands off to the
 * confirmation screen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements,
} from '@stripe/react-stripe-js';
import Icon from '../components/Icon';
import FeeSplit from '../components/FeeSplit';
import { EmptyState, Notice } from '../components/ui';
import { useBooking } from '../context/BookingContext';
import { api } from '../lib/api';
import { formatAmount } from '../lib/format';
import { trackEvent } from '../lib/consent';

/** loadStripe returns a promise that must be created once, not per render.
 * Keyed by publishable key so a config change is picked up on reload. */
const stripePromises = new Map();
const getStripePromise = (key) => {
  if (!stripePromises.has(key)) stripePromises.set(key, loadStripe(key));
  return stripePromises.get(key);
};

/** Map the site's own tokens into Stripe's Appearance API — one source of
 * truth for the palette, even inside the Stripe iframe. */
function siteAppearance() {
  const s = getComputedStyle(document.documentElement);
  const v = (name, fallback) => (s.getPropertyValue(name).trim() || fallback);
  return {
    theme: 'flat',
    variables: {
      colorPrimary: v('--primary', '#800020'),
      colorText: v('--text-primary', '#2c1810'),
      colorTextSecondary: v('--text-secondary', '#6e6a66'),
      colorBackground: v('--surface', '#ffffff'),
      colorDanger: v('--danger', '#c62828'),
      fontFamily: v('--font-sans', 'system-ui, sans-serif'),
      borderRadius: v('--radius-md', '12px'),
      spacingUnit: '4px',
    },
    rules: {
      '.Input': { border: `1px solid ${v('--border', '#e2dcd5')}`, boxShadow: 'none' },
      '.Input:focus': { border: `1px solid ${v('--primary', '#800020')}`, boxShadow: 'none' },
      '.Tab': { border: `1px solid ${v('--border', '#e2dcd5')}` },
      '.Tab--selected': { borderColor: v('--primary', '#800020') },
    },
  };
}

export default function CheckoutPay() {
  const { t } = useTranslation();
  const { reference } = useParams();

  const [order, setOrder] = useState(null);
  const [intent, setIntent] = useState(null); // { clientSecret, publishableKey, amountCents }
  const [error, setError] = useState('');
  const [notConfigured, setNotConfigured] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const { order: record } = await api.order(reference, controller.signal);
        setOrder(record);
        if (record.state !== 'pending_payment') return; // handled in render
        const pi = await api.orderPaymentIntent(reference);
        setIntent(pi);
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (err.status === 503) setNotConfigured(true);
        else setError(err.message || t('checkoutPay.loadError', 'We could not start the payment.'));
      }
    })();
    return () => controller.abort();
  }, [reference, t]);

  if (order && order.state !== 'pending_payment') {
    return <Navigate to={`/confirmation/${reference}`} replace />;
  }

  if (notConfigured) {
    return (
      <div className="shell shell--narrow section">
        <Notice tone="warn" icon="info" title={t('checkoutPay.unavailableTitle', 'Payments are not switched on yet')}>
          {t('checkoutPay.unavailableBody', 'Your offering is saved. We will be in touch to arrange the contribution.')}
        </Notice>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shell shell--narrow section">
        <EmptyState icon="alert" title={t('checkoutPay.errorTitle', 'We hit a snag starting the payment')}>
          {error}
        </EmptyState>
      </div>
    );
  }

  if (!order || !intent) {
    return (
      <div className="shell shell--narrow section">
        <div className="skeleton" style={{ height: 44, width: 220, marginBottom: 20, borderRadius: 8 }} />
        <div className="skeleton" style={{ height: 260, borderRadius: 'var(--radius-md)' }} />
      </div>
    );
  }

  const appearance = siteAppearance();
  return (
    <div className="shell shell--narrow" style={{ paddingBlock: 'var(--space-3xl) var(--space-5xl)' }}>
      <header className="stack stack--sm" style={{ marginBottom: 'var(--space-xl)' }}>
        <p className="eyebrow">{t('checkoutPay.eyebrow', 'Payment')}</p>
        <h1 className="display display--md">{t('checkoutPay.title', 'Complete your offering')}</h1>
        <p className="subtle">
          {t('checkoutPay.amountLine', {
            amount: formatAmount(intent.amountCents),
            monastery: order.monastery?.name,
            defaultValue: `${formatAmount(intent.amountCents)} · ${order.monastery?.name}`,
          })}
        </p>
        <FeeSplit
          offeringCents={order.offeringAmountCents}
          feeCents={order.platformFeeCents}
          monasteryName={order.monastery?.name}
        />
      </header>

      <Elements stripe={getStripePromise(intent.publishableKey)} options={{ clientSecret: intent.clientSecret, appearance }}>
        <PayForm reference={reference} order={order} amountCents={intent.amountCents} />
      </Elements>
    </div>
  );
}

function PayForm({ reference, order, amountCents }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clearDraft } = useBooking();
  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false); // webhook-wait state
  const [payError, setPayError] = useState('');
  const polling = useRef(false);

  const returnUrl = useMemo(
    () => `${window.location.origin}/checkout/pay/${reference}`,
    [reference],
  );

  /** Poll our own order until the webhook has moved it off pending_payment,
   * then go to the confirmation screen. Times out generously — the webhook
   * almost always beats this, and if it somehow doesn't the confirmation
   * screen's own "held" copy covers a processing payment. */
  const waitForPaidThenConfirm = useCallback(async () => {
    if (polling.current) return;
    polling.current = true;
    setConfirming(true);
    trackEvent('payment_authorized', {
      transaction_id: reference, value: amountCents / 100, currency: 'USD', item_id: order.service?.id,
    });
    const deadline = Date.now() + 25_000;
    while (Date.now() < deadline) {
      try {
        const { order: record } = await api.order(reference);
        if (record.state !== 'pending_payment') break;
      } catch { /* keep trying */ }
      await new Promise((r) => setTimeout(r, 1500));
    }
    clearDraft();
    navigate(`/confirmation/${reference}`, { replace: true });
  }, [reference, amountCents, order.service?.id, navigate, clearDraft]);

  // Returning from a redirect (3-D Secure, a wallet). Stripe appends
  // ?payment_intent=…&redirect_status=… — if it's here, resume.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('redirect_status');
    if (!status || !stripe) return;
    if (status === 'succeeded' || status === 'pending') {
      waitForPaidThenConfirm();
    } else if (status === 'failed') {
      setPayError(t('checkoutPay.redirectFailed', 'That payment did not go through. Try another card.'));
    }
  }, [stripe, waitForPaidThenConfirm, t]);

  const confirm = useCallback(async (extraConfirmParams) => {
    if (!stripe || !elements) return;
    setPayError('');
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
      ...extraConfirmParams,
    });
    if (error) {
      // A card decline / validation error keeps the donor here on the same
      // intent — they can fix it and try again without a new order.
      setPayError(error.message || t('checkoutPay.genericPayError', 'The payment could not be completed.'));
      trackEvent('payment_failed', { transaction_id: reference, reason: error.code });
      return false;
    }
    await waitForPaidThenConfirm();
    return true;
  }, [stripe, elements, returnUrl, reference, waitForPaidThenConfirm, t]);

  // The Elements group is created with a `clientSecret`, so this is the
  // immediate PaymentIntent flow — `confirmPayment` validates the form itself,
  // no `elements.submit()` step (that is the deferred-intent flow only).
  const onCardSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    await confirm();
    setSubmitting(false);
  };

  if (confirming) {
    return (
      <div className="stack stack--sm" style={{ alignItems: 'center', textAlign: 'center', padding: 'var(--space-3xl) 0' }}>
        <Icon name="lotus" size={28} />
        <p className="lede">{t('checkoutPay.confirming', 'Confirming your offering…')}</p>
        <p className="subtle">{t('checkoutPay.confirmingSub', 'This takes a moment. Please don’t close the page.')}</p>
      </div>
    );
  }

  return (
    <form className="stack stack--lg" onSubmit={onCardSubmit}>
      {payError && <Notice tone="danger" title={t('checkoutPay.notSent', 'Payment not completed')}>{payError}</Notice>}

      <ExpressCheckoutElement
        options={{ buttonHeight: 44 }}
        onConfirm={() => confirm()}
      />

      <PaymentElement options={{ layout: 'tabs' }} />

      <button type="submit" className="btn btn--primary btn--block" disabled={!stripe || submitting}>
        <Icon name="flame" size={17} solid />
        {submitting
          ? t('checkoutPay.paying', 'Processing…')
          : t('checkoutPay.pay', { amount: formatAmount(amountCents), defaultValue: `Pay ${formatAmount(amountCents)}` })}
      </button>

      <p className="subtle" style={{ textAlign: 'center' }}>
        {t('checkoutPay.securedBy', 'Card details go straight to Stripe and never touch GOMPA’s servers.')}
      </p>
    </form>
  );
}
